/**
 * Sidebar v2: the upstream-style default sidebar — flat pinned/active
 * shelves with a snoozed and settled tail, header search, a project-scope
 * picker, drag reorder, and pin/snooze — over forge's thread data and the
 * client-side overlay.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (default export, ~lines 2144-4967) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: forge's brand header, workspace
 * switcher, account footer, and update pill stay mounted exactly as in the
 * legacy layout (the ember stage artwork is forge's own); the merged thread
 * list comes from forge's live/replicated pipeline; the new-thread button
 * routes to forge's /new picker in multi-project setups and preselects the
 * active project otherwise; draft rows, file drops, per-environment
 * capability gating have no forge equivalent yet (documented seams).
 */
import { useEffect, useMemo, useRef, type MouseEvent as ReactMouseEvent } from "react";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { useShallow } from "zustand/react/shallow";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";

import {
  selectProjectsAcrossEnvironments,
  selectSidebarThreadsAcrossEnvironments,
  useStore,
} from "~/store";
import { useCmmdDeviceRegistrySnapshot } from "~/cmmd/cmmdDeviceRegistryResource";
import { useCmmdRuntimeInventory } from "~/cmmd/useCmmdRuntimeInventory";
import { useSavedEnvironmentRegistryStore } from "~/environments/runtime/catalog";
import { useSavedEnvironmentRuntimeStore } from "~/environments/runtime/savedEnvironmentRuntimeStore";
import { usePrimaryEnvironmentDescriptor, usePrimaryEnvironmentId } from "~/environments/primary";
import { getStoredLocalDeviceKey } from "~/deviceRegistryIdentity";
import { useSidebarOrchestration } from "~/components/sidebar/useSidebarOrchestration";
import { useSidebarDeviceDisconnect } from "~/components/sidebar/useSidebarDeviceDisconnect";
import { useResolveEnvironmentLabel } from "~/components/sidebar/useResolveEnvironmentLabel";
import { buildProjectDisplayByKey } from "~/components/sidebar/projectDisplayIndex";
import { overlayCanonicalThreadDispositions } from "~/components/sidebar/canonicalThreadDispositionOverlay";
import { mergeLiveAndReplicatedSidebarThreads } from "~/components/sidebar/mergeSidebarThreadHistory";
import { SettingsSidebarNav } from "~/components/settings/SettingsSidebarNav";
import { SidebarContent, SidebarGroup, useSidebar } from "~/components/ui/sidebar";
import { SidebarAccountFooter } from "~/components/sidebar/SidebarAccountFooter";
import { SidebarUpdatePill } from "~/components/sidebar/SidebarUpdatePill";
import { ForgeWorkspaceSwitcher } from "~/components/identity/ForgeWorkspaceSwitcher";
import { resolveThreadRouteTarget } from "~/threadRoutes";
import { useSettings } from "~/hooks/useSettings";

import { useSidebarV2Controller } from "./sidebarV2Controller";
import { handleThreadSearchKeyDown, threadSearchMatchKey } from "./sidebarV2Search";
import { SidebarV2Header } from "./sidebarV2Header";
import { SidebarV2ProjectScope } from "./SidebarV2ProjectScope";
import { SidebarV2SearchResultRow } from "./sidebarV2SearchRow";
import { SidebarV2ThreadList } from "./SidebarV2ThreadList";
import { CustomSnoozeDialogHost } from "./sidebarV2CustomSnoozeDialog";
import { sidebarProjectScopeMemberKeys, sidebarThreadKey } from "./sidebarV2Threads";
import { buildProjectGroupNameByKey, buildRowThreadProps } from "./sidebarV2RowThreadProps";
import { mergeSidebarV2Projects } from "./sidebarV2Projects";

/** Compose live and replicated threads into the v2 sidebar, with search, project scope, and account controls. */
// fallow-ignore-next-line complexity -- v2 orchestration hook composing focused sub-hooks (see sidebarV2* modules)
export default function SidebarV2() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const isOnSettings = pathname === "/settings" || pathname.startsWith("/settings/");
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();

  const orchestration = useSidebarOrchestration();
  const timestampFormat = useSettings((s) => s.timestampFormat);

  // The merged thread list: live threads plus the replicated ones from other
  // runtimes, the same source the legacy rows key their handlers against.
  const storeSnapshot = useStore(
    useShallow((state) => ({
      activeEnvironmentId: state.activeEnvironmentId,
      environmentStateById: state.environmentStateById,
    })),
  );
  const sidebarThreadsBase = useMemo(
    () => selectSidebarThreadsAcrossEnvironments(storeSnapshot),
    [storeSnapshot],
  );
  const deviceRegistrySnapshot = useCmmdDeviceRegistrySnapshot();
  const deviceDisconnect = useSidebarDeviceDisconnect();
  const runtimeInventory = useCmmdRuntimeInventory({
    registeredRuntimes: deviceRegistrySnapshot.registry?.runtimes ?? [],
    registeredDevices: deviceRegistrySnapshot.registry?.devices ?? [],
  });
  const savedEnvironmentRegistry = useSavedEnvironmentRegistryStore((state) => state.byId);
  const savedEnvironmentRuntimeById = useSavedEnvironmentRuntimeStore((state) => state.byId);
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const primaryEnvironmentDescriptor = usePrimaryEnvironmentDescriptor();
  const resolveEnvironmentLabel = useResolveEnvironmentLabel({
    savedEnvironmentRegistry,
    savedEnvironmentRuntimeById,
    deviceRegistry: deviceRegistrySnapshot.registry,
    currentRegisteredRuntimeId: runtimeInventory.currentRegisteredRuntimeId,
    currentRegisteredDeviceKey: getStoredLocalDeviceKey(),
    runtimeInventoryRuntimes: runtimeInventory.runtimes,
    primaryEnvironmentId,
    primaryEnvironmentLabel: primaryEnvironmentDescriptor?.label ?? null,
  });
  const allSidebarThreads = useMemo(
    () =>
      mergeLiveAndReplicatedSidebarThreads(
        overlayCanonicalThreadDispositions(
          sidebarThreadsBase,
          deviceDisconnect.disconnectedRuntimeIds,
          runtimeInventory.threadDispositions ?? [],
          savedEnvironmentRegistry,
        ),
        runtimeInventory.sidebarThreads,
        savedEnvironmentRegistry,
      ),
    [
      deviceDisconnect.disconnectedRuntimeIds,
      runtimeInventory.sidebarThreads,
      runtimeInventory.threadDispositions,
      savedEnvironmentRegistry,
      sidebarThreadsBase,
    ],
  );

  const sidebarProjects = useMemo(
    () =>
      mergeSidebarV2Projects({
        live: orchestration.sortedProjects,
        inventory: runtimeInventory.sidebarProjects ?? [],
        environments: savedEnvironmentRegistry,
      }),
    [orchestration.sortedProjects, runtimeInventory.sidebarProjects, savedEnvironmentRegistry],
  );
  const projectEntries = useMemo(
    () =>
      sidebarProjects.map((project) => ({
        projectKey: project.projectKey,
        displayName: project.name,
        environmentId: project.environmentId,
        projectId: project.id,
        cwd: project.cwd,
        memberKeys: sidebarProjectScopeMemberKeys(project),
        memberRefs: [
          { environmentId: project.environmentId, projectId: project.id },
          ...(project.memberProjectRefs ?? []),
        ],
      })),
    [sidebarProjects],
  );
  const projectDisplayByKey = useMemo(
    () =>
      buildProjectDisplayByKey(
        [...selectProjectsAcrossEnvironments(storeSnapshot), ...sidebarProjects],
        [],
        savedEnvironmentRegistry,
      ),
    [storeSnapshot, sidebarProjects, savedEnvironmentRegistry],
  );
  const projectDisplayNameByKey = useMemo(
    () => buildProjectGroupNameByKey(projectEntries, savedEnvironmentRegistry),
    [projectEntries, savedEnvironmentRegistry],
  );

  const routeTarget = useParams({
    strict: false,
    select: (params) => resolveThreadRouteTarget(params),
  });
  const routeThreadRef = routeTarget?.kind === "server" ? routeTarget.threadRef : null;
  const routeThreadKey = routeThreadRef === null ? null : scopedThreadKey(routeThreadRef);

  const controller = useSidebarV2Controller({
    allSidebarThreads,
    projectEntries,
    isProjectInventoryLoading: runtimeInventory.isLoading || !runtimeInventory.hasLoaded,
    timestampFormat,
    terminalOpen: false,
    routeThreadRef,
  });

  // Thread-jump hints reuse the shared shortcut-hook instance mounted by
  // the orchestration hook: its single global keydown/keyup listener drives
  // visibility, and its label map is keyed by the same canonical scoped
  // keys the v2 rows use, so the badge a row shows is the ⌘N the shared
  // listener actually navigates by. A second listener here would double the
  // hint sync.
  const showThreadJumpHints = orchestration.visibleThreadJumpLabelByKey.size > 0;
  const jumpLabelByKey = orchestration.visibleThreadJumpLabelByKey;

  const headerSearchRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!controller.isSearchingThreads || controller.threadSearchResults.length === 0) return;
    document
      .getElementById(`sidebar-thread-search-result-${controller.activeSearchResultIndex}`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [
    controller.activeSearchResultIndex,
    controller.isSearchingThreads,
    controller.threadSearchResults,
  ]);

  /** Open the new-thread route, preselecting the current project for Shift-click or a single project. */
  const handleNewThreadClick = (event: ReactMouseEvent) => {
    if (isMobile) setOpenMobile(false);
    // One project: nothing to pick, go straight to the compose landing with
    // the project preselected. Multi-project plain clicks open the same
    // landing unpreselected, which is forge's own picker surface.
    const target = orchestration.newThreadRoute;
    if (target !== null && (event.shiftKey || projectEntries.length <= 1)) {
      void navigate(target);
      return;
    }
    void navigate({ to: "/new" });
  };

  if (isOnSettings) {
    return (
      <>
        <SettingsSidebarNav pathname={pathname} />
        <ForgeWorkspaceSwitcher />
        <SidebarAccountFooter />
      </>
    );
  }

  return (
    <>
      <SidebarContent
        className="gap-0"
        data-slot="sidebar-v2-content"
        fixedHeader={
          <SidebarGroup className="z-[1] flex-none" data-testid="sidebar-v2-header">
            <SidebarV2Header
              searchFieldRef={headerSearchRef}
              hasProjects={projectEntries.length > 0}
              projectScope={
                <SidebarV2ProjectScope
                  projects={projectEntries}
                  selectedKey={controller.projectScopeKey}
                  onSelect={controller.setProjectScopeKey}
                  anchorRef={headerSearchRef}
                />
              }
              onNewProject={() => {
                if (isMobile) setOpenMobile(false);
                void navigate({ to: "/new" });
              }}
              onNewThread={handleNewThreadClick}
              newThreadDisabled={projectEntries.length === 0}
              newThreadShortcutLabel={orchestration.newThreadShortcutLabel}
              newThreadInProjectShortcutLabel={null}
              showNewThreadInProjectHint={projectEntries.length > 1}
              searchInputRef={searchInputRef}
              searchQuery={controller.threadSearchQuery}
              onSearchQueryChange={controller.setThreadSearchQuery}
              onSearchKeyDown={(event) => {
                handleThreadSearchKeyDown(event, {
                  resultCount: controller.threadSearchResults.length,
                  activeSearchResultIndex: controller.activeSearchResultIndex,
                  onActiveIndexChange: controller.setActiveSearchResultIndex,
                  onSelectResult: (index) => {
                    const thread = controller.threadSearchResults[index];
                    if (thread) controller.selectThreadSearchResult(thread);
                  },
                  onClearSearch: controller.clearThreadSearch,
                });
              }}
              isSearching={controller.isSearchingThreads}
              searchResultCount={controller.threadSearchResults.length}
              activeSearchResultIndex={controller.activeSearchResultIndex}
              onClearSearch={controller.clearThreadSearch}
            />
          </SidebarGroup>
        }
      >
        {controller.isSearchingThreads ? (
          <SidebarGroup className="min-h-0 flex-1" data-testid="sidebar-v2-search-results">
            {controller.threadSearchResults.length > 0 ? (
              <ul
                id="sidebar-thread-search-results"
                role="listbox"
                aria-label="Thread search results"
                className="flex flex-col gap-px"
              >
                {controller.threadSearchResults.map((thread, index) => {
                  const threadKey = sidebarThreadKey(thread);
                  const rowContext = buildRowThreadProps(
                    {
                      projectDisplayNameByKey,
                      projectDisplayByKey,
                      savedEnvironmentRegistry,
                      resolveEnvironmentLabel,
                      currentEnvironmentId: primaryEnvironmentId,
                    },
                    thread,
                  );
                  return (
                    <SidebarV2SearchResultRow
                      key={threadKey}
                      thread={thread}
                      projectDisplayName={rowContext.projectDisplayName}
                      projectCwd={rowContext.projectCwd}
                      projectCanonicalIdentity={rowContext.projectCanonicalIdentity}
                      searchMatch={
                        controller.contentSearchMatches?.get(
                          threadSearchMatchKey({
                            environmentId: thread.environmentId,
                            threadId: thread.id,
                          }),
                        ) ?? null
                      }
                      environmentLabel={rowContext.environmentLabel}
                      environmentMachine={rowContext.threadOrigin}
                      modelLabel={thread.modelSelection?.model ?? null}
                      linkedPrUrl={rowContext.linkedPrUrl}
                      threadOrigin={rowContext.threadOrigin}
                      isHighlighted={controller.activeSearchResultIndex === index}
                      isRouteActive={routeThreadKey === threadKey}
                      resultId={`sidebar-thread-search-result-${index}`}
                      searchQuery={controller.threadSearchQuery}
                      onHighlight={() => controller.setActiveSearchResultIndex(index)}
                      onSelect={() => controller.selectThreadSearchResult(thread)}
                    />
                  );
                })}
              </ul>
            ) : (
              <p
                role="status"
                className="px-2 py-6 text-center text-xs text-sidebar-muted-foreground"
              >
                {(controller.isContentSearchPending ?? false)
                  ? "Searching thread messages…"
                  : "No threads found"}
              </p>
            )}
          </SidebarGroup>
        ) : (
          <SidebarV2ThreadList
            controller={controller}
            projectDisplayNameByKey={projectDisplayNameByKey}
            projectDisplayByKey={projectDisplayByKey}
            savedEnvironmentRegistry={savedEnvironmentRegistry}
            resolveEnvironmentLabel={resolveEnvironmentLabel}
            currentEnvironmentId={primaryEnvironmentId}
            routeThreadKey={routeThreadKey}
            showThreadJumpHints={showThreadJumpHints}
            jumpLabelByKey={jumpLabelByKey}
          />
        )}
      </SidebarContent>
      <SidebarUpdatePill />
      <ForgeWorkspaceSwitcher />
      <SidebarAccountFooter />
      <CustomSnoozeDialogHost />
    </>
  );
}
